// Translation Helper Hooks
// Provides easy-to-use hooks for translating form elements
import { useTranslation } from '../contexts/translation-context';
import type { FormField, FormSection } from '../types/form-builder.types';

/**
 * Hook to translate form title and description
 */
export const useFormTranslation = (formId: number, form: { title: string; description?: string }) => {
    const { translate } = useTranslation();

    return {
        title: translate('form', formId, 'title', form.title),
        description: form.description
            ? translate('form', formId, 'description', form.description)
            : undefined,
    };
};

/**
 * Hook to translate a section
 */
export const useSectionTranslation = (section: FormSection) => {
    const { translate } = useTranslation();

    return {
        ...section,
        title: translate('section', parseInt(section.id), 'title', section.title),
        description: section.description
            ? translate('section', parseInt(section.id), 'description', section.description)
            : undefined,
    };
};

/**
 * Hook to translate a field
 */
export const useFieldTranslation = (field: FormField) => {
    const { translate } = useTranslation();
    const fieldId = parseInt(field.id);

    // Translate basic field properties
    const translatedConfig = {
        ...field.config,
        label: translate('field', fieldId, 'label', field.config.label || ''),
        placeholder: field.config.placeholder
            ? translate('field', fieldId, 'placeholder', field.config.placeholder)
            : undefined,
        helpText: field.config.helpText
            ? translate('field', fieldId, 'help_text', field.config.helpText)
            : undefined,
    };

    // Translate NPS labels
    if (field.config.lowLabel) {
        translatedConfig.lowLabel = translate('field', fieldId, 'low_label', field.config.lowLabel);
    }
    if (field.config.highLabel) {
        translatedConfig.highLabel = translate('field', fieldId, 'high_label', field.config.highLabel);
    }

    // Translate quiz feedback
    if (field.config.explanation) {
        translatedConfig.explanation = translate('field', fieldId, 'explanation', field.config.explanation);
    }
    if (field.config.correctFeedback) {
        translatedConfig.correctFeedback = translate('field', fieldId, 'correct_feedback', field.config.correctFeedback);
    }
    if (field.config.incorrectFeedback) {
        translatedConfig.incorrectFeedback = translate('field', fieldId, 'incorrect_feedback', field.config.incorrectFeedback);
    }

    // Translate options
    if (field.config.options && Array.isArray(field.config.options)) {
        translatedConfig.options = field.config.options.map((option) => ({
            ...option,
            // Use option.id directly as entity_id (can be string or number)
            label: translate('option', option.id, 'label', option.label || ''),
        }));
    }

    // Translate matrix rows
    if (field.config.rows && Array.isArray(field.config.rows)) {
        translatedConfig.rows = field.config.rows.map((row) => ({
            ...row,
            // Use row.id directly as entity_id (can be string or number)
            label: translate('row', row.id, 'label', row.label || ''),
        }));
    }

    // Translate matrix columns
    if (field.config.columns && Array.isArray(field.config.columns)) {
        translatedConfig.columns = field.config.columns.map((column) => ({
            ...column,
            // Use column.id directly as entity_id (can be string or number)
            label: translate('column', column.id, 'label', column.label || ''),
        }));
    }

    return {
        ...field,
        config: translatedConfig,
    };
};

/**
 * Hook to translate an entire form structure
 */
export const useFormStructureTranslation = (
    formId: number,
    form: { title: string; description?: string; settings?: any },
    sections: FormSection[]
) => {
    const formTranslation = useFormTranslation(formId, form);

    const translatedSections = sections.map((section) => {
        const sectionTranslation = useSectionTranslation(section);

        // Translate all fields in all rows
        const translatedRows = section.rows.map((row) => ({
            ...row,
            fields: row.fields.map((field) => useFieldTranslation(field)),
        }));

        return {
            ...sectionTranslation,
            rows: translatedRows,
        };
    });

    return {
        ...form,
        ...formTranslation,
        sections: translatedSections,
    };
};

